import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Button, Row, Col, Typography, Space, Divider } from "antd";
import {
  UserOutlined, MailOutlined, PhoneOutlined, SaveOutlined, CloseOutlined,
  IdcardOutlined, TeamOutlined, BankOutlined, UserAddOutlined
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { LOGGED_USER } from "../../variables/constants";
import axiosInstance from "../../variables/api/axiosInstance";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Thème (aligné sur EditIntermediaire pour une expérience cohérente)
const colors = {
  primary: "#242166",
  secondary: "#1890ff",
  gradient: "linear-gradient(135deg, #242166 0%, #1890ff 100%)",
};

const AddIntermediaire = ({ show, handleClose, editMode, organisme }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [mailExists, setMailExists] = useState(false);
  const [codeIntermediaireExists, setCodeIntermediaireExists] = useState(false);

  const typePersonne = Form.useWatch("typePersonne", form);
  const isMorale = typePersonne === "MORALE";

  const orgForChecks = organisme || LOGGED_USER?.profil?.organisme;

  const resetForm = () => {
    form.resetFields();
    setMailExists(false);
    setCodeIntermediaireExists(false);
  };

  const closeModal = () => {
    resetForm();
    handleClose && handleClose();
  };

  useEffect(() => {
    if (!show && !editMode) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, editMode]);

  // Attribution automatique des taux de commission par défaut (par type + organisme)
  const assignDefaultCommissionRates = async (newIntermediaire, type) => {
    try {
      const organismeCode = newIntermediaire?.organisme?.code || organisme?.code || LOGGED_USER?.profil?.organisme?.code;
      const intermediaireType = (newIntermediaire?.type || type || "").toLowerCase();
      if (!newIntermediaire?.codeId || !organismeCode || !intermediaireType) return;

      const { data } = await axiosInstance.get(`/api/taux-commission`);
      const defaultRates = (data || []).filter((tc) => {
        const tcType = (typeof tc?.typeIntermediaire === "string"
          ? tc.typeIntermediaire
          : tc?.typeIntermediaire?.libelle || tc?.typeIntermediaire?.nom || ""
        ).toLowerCase();
        const tcOrgCode = tc?.organisme?.code || tc?.organisme?.codeOrganisme;
        return tcType === intermediaireType && tcOrgCode === organismeCode;
      });
      if (!defaultRates.length) return;

      await axiosInstance.put(`/api/intermediaire/taux-commission`, {
        intermediaireId: newIntermediaire.codeId,
        commissions: defaultRates.map((tc) => ({ categorie: tc.categorie, taux: tc.taux })),
      });
    } catch (error) {
      console.log(error);
    }
  };

  const checkIfEmailExists = async (email) => {
    try {
      const res = await axiosInstance.get(`/api/utilisateur/check/${email}`);
      if (res.data) {
        toast.warning("Il existe déjà un utilisateur avec cette adresse mail dans le système !");
      }
      setMailExists(!!res.data);
      return !!res.data;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  const checkIfCodeIntermediaireExists = async (code) => {
    if (!code) {
      setCodeIntermediaireExists(false);
      return false;
    }
    try {
      const res = await axiosInstance.get(`/api/intermediaire/code-check/${code}/${orgForChecks?.code}`);
      if (res.data) {
        toast.warning("Il existe déjà un intermédiaire avec ce code dans la compagnie !");
      }
      setCodeIntermediaireExists(!!res.data);
      return !!res.data;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    if (!value) { setMailExists(false); return; }
    const fullEmail = organisme?.domaine ? `${value}@${organisme?.domaine}` : value;
    if (fullEmail.length > 3) checkIfEmailExists(fullEmail);
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const morale = values.typePersonne === "MORALE";

      // Email facultatif : suffixe domaine + vérification seulement s'il est renseigné
      let email = values.email || "";
      if (email && organisme?.domaine) email += "@" + organisme?.domaine;
      if (email) {
        const emailExists = await checkIfEmailExists(email);
        if (emailExists) { setLoading(false); return; }
      }

      const codeExists = await checkIfCodeIntermediaireExists(values.codeIntermediaire);
      if (codeExists) { setLoading(false); return; }

      const user = {
        nom: morale ? values.raisonSociale : values.nom,
        prenom: morale ? "" : values.prenom,
        email: email || null,
        contact: values.telephone ? [values.telephone] : [],
        genre: morale ? null : values.genre,
        adresse: values.adresseResidence,
        fonction: "INTERMEDIAIRE",
        type: values.type,
        typePersonne: values.typePersonne,
        numeroRegistreCommerce: morale ? values.numeroRegistreCommerce : null,
        nif: morale ? values.nif : null,
        organisme: orgForChecks,
        username: values.codeIntermediaire,
      };

      const agenceCode = orgForChecks?.code;
      const res = await axiosInstance.post(
        `/api/intermediaire/new/${agenceCode}`,
        JSON.stringify(user)
      );
      await assignDefaultCommissionRates(res.data, values.type);
      toast.success("Intermédiaire ajouté avec succès");
      closeModal();
    } catch (err) {
      console.log(err);
      toast.error("Erreur lors de l'ajout de l'intermédiaire");
    } finally {
      setLoading(false);
    }
  };

  const phoneValidator = (_, value) => {
    const phonePattern = /^(22|90|91|92|93|96|98|99|70|71|79)\d{6}$/;
    if (!value) return Promise.resolve(); // téléphone facultatif
    return phonePattern.test(value)
      ? Promise.resolve()
      : Promise.reject(new Error("Numéro de téléphone invalide"));
  };

  const labelStrong = (txt, extra) => (
    <Text strong style={{ color: colors.primary }}>
      {txt}{extra ? <Text type="secondary" style={{ fontWeight: 400 }}> {extra}</Text> : null}
    </Text>
  );

  return (
    <Modal
      open={show}
      onCancel={closeModal}
      footer={null}
      width={900}
      centered
      style={{ top: 20 }}
      destroyOnClose
      closeIcon={<CloseOutlined style={{ color: colors.primary, fontSize: "18px" }} />}
    >
      <div style={{ padding: "8px 0" }}>
        {/* Header */}
        <div style={{
          textAlign: "center",
          marginBottom: "32px",
          paddingBottom: "24px",
          borderBottom: `3px solid ${colors.primary}`,
        }}>
          <div style={{
            width: "64px",
            height: "64px",
            borderRadius: "50%",
            background: colors.gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
            boxShadow: "0 4px 12px rgba(36, 33, 102, 0.3)",
          }}>
            <UserAddOutlined style={{ fontSize: "32px", color: "white" }} />
          </div>
          <Title level={3} style={{ margin: "0 0 8px 0", color: colors.primary }}>
            Ajouter un intermédiaire
          </Title>
          <Text style={{ color: "#8c8c8c", fontSize: "14px" }}>
            Enregistrer un nouvel intermédiaire de l'agence
          </Text>
        </div>

        {/* Form */}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark="optional"
          initialValues={{ typePersonne: "PHYSIQUE" }}
        >
          <Row gutter={16}>
            {/* Nature de l'intermédiaire */}
            <Col xs={24}>
              <Form.Item name="typePersonne" label={labelStrong("Nature de l'intermédiaire")}>
                <Select size="large">
                  <Option value="PHYSIQUE">Personne physique</Option>
                  <Option value="MORALE">Personne morale</Option>
                </Select>
              </Form.Item>
            </Col>

            {isMorale ? (
              <>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="raisonSociale"
                    label={labelStrong("Raison sociale")}
                    rules={[{ required: true, message: "Raison sociale requise (2 caractères min.)" }, { min: 2, message: "2 caractères minimum" }]}
                  >
                    <Input prefix={<BankOutlined style={{ color: colors.secondary }} />} placeholder="Raison sociale de l'intermédiaire" size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="numeroRegistreCommerce" label={labelStrong("Registre de commerce (RCCM)")}>
                    <Input placeholder="N° registre de commerce" size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="nif" label={labelStrong("NIF")}>
                    <Input placeholder="Numéro d'identification fiscale" size="large" />
                  </Form.Item>
                </Col>
              </>
            ) : (
              <>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="nom"
                    label={labelStrong("Nom")}
                    rules={[{ required: true, message: "Nom requis" }, { min: 2, message: "Le nom doit avoir au moins 2 caractères" }]}
                  >
                    <Input prefix={<UserOutlined style={{ color: colors.secondary }} />} placeholder="Entrez le nom" size="large" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="prenom"
                    label={labelStrong("Prénom(s)")}
                    rules={[{ required: true, message: "Prénom requis" }, { min: 2, message: "Le prénom doit avoir au moins 2 caractères" }]}
                  >
                    <Input prefix={<UserOutlined style={{ color: colors.secondary }} />} placeholder="Entrez le prénom" size="large" />
                  </Form.Item>
                </Col>
              </>
            )}

            {/* Code intermédiaire */}
            <Col xs={24} sm={12}>
              <Form.Item
                name="codeIntermediaire"
                label={labelStrong("Code de l'intermédiaire")}
                validateStatus={codeIntermediaireExists ? "error" : undefined}
                help={codeIntermediaireExists ? "Ce code est déjà utilisé dans la compagnie" : undefined}
                rules={[{ required: true, message: "Code intermédiaire requis" }]}
              >
                <Input
                  prefix={<IdcardOutlined style={{ color: colors.secondary }} />}
                  placeholder="Entrez le code intermédiaire"
                  size="large"
                  onChange={(e) => checkIfCodeIntermediaireExists(e.target.value)}
                />
              </Form.Item>
            </Col>

            {/* Genre (physique uniquement) */}
            {!isMorale && (
              <Col xs={24} sm={12}>
                <Form.Item name="genre" label={labelStrong("Genre")} rules={[{ required: true, message: "Genre requis" }]}>
                  <Select placeholder="Sélectionnez le genre" size="large">
                    <Option value="MASCULIN">Masculin</Option>
                    <Option value="FEMININ">Féminin</Option>
                  </Select>
                </Form.Item>
              </Col>
            )}

            {/* Type d'intermédiaire */}
            <Col xs={24} sm={12}>
              <Form.Item
                name="type"
                label={labelStrong("Type d'intermédiaire")}
                rules={[{ required: true, message: "Veuillez sélectionner le type d'intermédiaire" }]}
              >
                <Select
                  placeholder="Sélectionnez le type d'intermédiaire"
                  size="large"
                  suffixIcon={<TeamOutlined style={{ color: colors.secondary }} />}
                >
                  <Option value="AGENT_GENERAL">Agent général</Option>
                  <Option value="APPORTEUR_REGULIER">Apporteur régulier</Option>
                  <Option value="APPORTEUR_OCCASIONNEL">Apporteur occasionnel</Option>
                </Select>
              </Form.Item>
            </Col>

            {/* Email */}
            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label={labelStrong("Adresse mail", "(facultatif)")}
                validateStatus={mailExists ? "error" : undefined}
                help={mailExists ? "Adresse déjà utilisée par un utilisateur" : undefined}
                rules={[{ type: "email", message: "L'adresse email n'est pas valide" }]}
              >
                <Input
                  prefix={<MailOutlined style={{ color: colors.secondary }} />}
                  suffix={organisme?.domaine && <Text style={{ color: "#8c8c8c" }}>@{organisme?.domaine}</Text>}
                  placeholder="Veuillez renseigner l'adresse mail"
                  size="large"
                  onChange={handleEmailChange}
                />
              </Form.Item>
            </Col>

            {/* Téléphone */}
            <Col xs={24} sm={12}>
              <Form.Item
                name="telephone"
                label={labelStrong("Numéro de téléphone", "(facultatif)")}
                rules={[{ validator: phoneValidator }]}
              >
                <Input
                  prefix={<PhoneOutlined style={{ color: colors.secondary }} />}
                  addonBefore={<Text strong>TG (+228)</Text>}
                  placeholder="00 00 00 00"
                  size="large"
                  maxLength={8}
                />
              </Form.Item>
            </Col>

            {/* Adresse */}
            <Col xs={24}>
              <Form.Item name="adresseResidence" label={labelStrong("Adresse de résidence")}>
                <TextArea placeholder="L'adresse de résidence de l'intermédiaire" maxLength={255} rows={3} showCount />
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: "24px 0", borderColor: "#f0f0f0" }} />

          <Form.Item style={{ marginBottom: 0, textAlign: "center" }}>
            <Space size="middle">
              <Button size="large" onClick={closeModal} icon={<CloseOutlined />} style={{ minWidth: "120px", height: "44px" }}>
                Annuler
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
                icon={<SaveOutlined />}
                style={{
                  background: colors.gradient,
                  border: "none",
                  minWidth: "140px",
                  height: "44px",
                  boxShadow: "0 4px 12px rgba(36, 33, 102, 0.3)",
                }}
              >
                {loading ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default AddIntermediaire;
